const installation = {
    name: "Installation",
    path: "installation",
    title: "Get started with Unicss",
    heading: false,
    description: "Unicss works by scanning all of your HTML files, JavaScript components, and any other templates for class names, generating the corresponding styles and then writing them to a static CSS file.",

    exampleData: [
        {
            "title": "Choose your installation method",
            "description": "",
            "moreDescription": "Select the installation method that best fits your project setup and workflow.",
            "parentClass": "!bg-none !border-0",
            "example": {
                "element": "div",
                "className": "relative",
                "code": "",
                "tabs": true,
                "tabData": {
                    "vite": {
                        "title": "Vite",
                        "content": `# Create your project with Vite
npm create vite@latest my-project
cd my-project

# Install Unicss
npm install @unizap/unicss

# Add to vite.config.js
import { defineConfig } from 'vite';
import unicss from '@unizap/unicss/vite-plugin-unicss';

export default defineConfig({
  plugins: [
    unicss({
      output: 'src/unicss.css',  // Output path for generated CSS
      skipBase: false             // Optional: skip base styles
    })
  ]
});`,
                        "description": "Automatic CSS generation with HMR support"
                    },
"PostCSS": {
    "title": "PostCSS",
    "content": `# Install Unicss and PostCSS dependencies
npm install @unizap/unicss postcss autoprefixer glob 

# app/globals.css // include Unicss styles
@unicss;    

# postcss.config.js
module.exports = {
  plugins: {
    '@unizap/unicss/postcss': {},
    autoprefixer: {}
  }
}

# app/layout.tsx or main.tsx
import './globals.css'
`,
    "description": "Use Unicss as a PostCSS plugin for advanced CSS processing in your build pipeline."
},
                    "cli": {
                        "title": "Unicss CLI",
                        "content": `# Install Unicss via npm
npm i @unizap/unicss

# Generate CSS in style/unicss.css
npx unicss

# Generate and watch CSS in a specified path
npx unicss -w -o src/output.css

# Generate without base styles
npx unicss -o src/output.css --skip-base`,
                        "description": "Traditional approach using npm and CLI commands"
                    },
                    
                    "cdn": {
                        "title": "CDN",
                        "content": `<!-- Add script tag to your HTML -->
<script src="https://unpkg.com/@unizap/unicss/dist/unicss.min.js"></script>

<!-- Or use a specific version -->
<script src="https://unpkg.com/@unizap/unicss@2.0.0/dist/unicss.min.js"></script>

<!-- Initialize UniCSS -->
<script>
    document.addEventListener('DOMContentLoaded', function () {
        UniCSS.init();
    });
</script>

<!-- Advanced configuration (optional) -->
<script>
    document.addEventListener('DOMContentLoaded', function () {
        UniCSS.init({
            theme: {
                extend: {
                    colors: { 'color-primary': '#3b82f6' },
                    spacing: { '72': '18rem', '84': '21rem', '96': '24rem' },
                    breakpoints: { '3xl': '1600px' }
                }
            }
        });
    });
</script>`,
                        "description": "Perfect for prototyping and projects without build tools. Not for production use."
                    }
                }
            }
        },
        {
            "title": "Start using Unicss in your HTML",
            "description": "",
            "moreDescription": "Add the CSS file to your HTML and start using Unicss utility classes.",
            "parentClass": "!bg-none !border-0",
            "example": {
                "element": "div",
                "className": "relative",
                "code": "",
                "tabs": true,
                "tabData": {
                    "vite": {
                        "title": "Vite Usage",
                        "content": `<!doctype html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <!-- Link to Vite-generated CSS file -->
  <link href="./src/unicss.css" rel="stylesheet">
</head>
<body>
  <h1 class="text-3xl font-bold underline text-color-blue-600">
    Hello world!
  </h1>
  <p class="mt-4 text-color-gray-700">
    Welcome to Unicss with Vite!
  </p>
</body>
</html>`,
                        "description": "Link to CSS file generated by Vite plugin"
                    },
                    "PostCSS": {
                        "title": "PostCSS Usage",
                        "content": `import type { Metadata } from "next";
import "./globals.css";
import { ThemeProvider } from "@/lib/themeContext";

export default function RootLayout() {
  return (
    <html lang="en">
      <body>
        <ThemeProvider>
          {children}
        </ThemeProvider>
      </body>
    </html>
  );
}
`,
                        "description": "Import the global CSS file that includes Unicss styles  "
                    },
                    "cli": {
                        "title": "CLI Usage",
                        "content": `<!doctype html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <!-- Link to your generated CSS file -->
  <link href="./style/unicss.css" rel="stylesheet">
</head>
<body>
  <h1 class="text-3xl font-bold underline text-color-blue-600">
    Hello world!
  </h1>
  <p class="mt-4 text-color-gray-700">
    Welcome to Unicss with CLI!
  </p>
</body>
</html>`,
                        "description": "Link to the CSS file generated by CLI"
                    },
                    
                    "cdn": {
                        "title": "CDN Usage",
                        "content": `<!doctype html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <!-- Include CDN script -->
  <script src="https://unpkg.com/@unizap/unicss/dist/unicss.min.js"></script>
</head>
<body>
  <h1 class="text-3xl font-bold underline text-color-blue-600">
    Hello world!
  </h1>
  <p class="mt-4 text-color-gray-700">
    Welcome to Unicss with CDN!
  </p>
  
  <!-- Initialize UniCSS -->
  <script>
    document.addEventListener('DOMContentLoaded', function () {
      UniCSS.init();
    });
  </script>
</body>
</html>`,
                        "description": "Include script and initialize - no build process needed"
                    }
                }
            }
        },
        {
            "title": "Unicss Helper for VS Code",
            "description": "",
            "moreDescription": `The official <a class=\"font-semibold text-color-blue-500 underline hover:decoration-thick-[2px]\" href=\"https://marketplace.visualstudio.com/items?itemName=Unizap.unicss\" target=\"_blank\">Unicss Helper</a> extension for Visual Studio Code enhances the development experience by providing users with advanced features such as autocomplete, syntax highlighting, and linting.`,
            "parentClass": "!bg-none !border-0",
            "example": {
                "element": "div",
                "className": "relative",
                "code": `<!-- Install via VS Code Extensions -->
1. Open VS Code
2. Go to Extensions (Ctrl+Shift+X)
3. Search for "Unicss Helper"
4. Click Install

<!-- Or install via command line -->
code --install-extension unizap.unicss-helper`
            }
        }
    ]
};

export default installation;